/**
 * Which note resources carry `counts_as_touch`.
 *
 * WHY THIS IS A FUNCTION AND NOT AN `if` IN THE COMPONENT
 * -------------------------------------------------------
 * `NoteInputs` is shared: react-admin mounts it under `contact_notes` on a
 * contact page and under `deal_notes` on a deal page, with the same JSX. Only
 * `contact_notes` has this column -- 048_lifecycle_core.sql adds it there and
 * nowhere else, because a touch is contact with a PERSON and a deal is not a
 * person.
 *
 * Get the gate wrong and the failure is not a missing checkbox. A registered
 * react-admin input is submitted whether or not the user touches it, so
 * rendering this on a deal note posts `counts_as_touch` to a table with no such
 * column, PostgREST rejects the whole insert, and every deal note save breaks
 * with a schema error. The blast radius is a feature nobody was editing.
 *
 * Pulling the rule out here makes that testable in Node, and gives the mistake
 * one place to be made rather than one per call site -- there are two, and a
 * third is exactly the kind of thing an upstream merge adds.
 */

/** Resources whose table actually has the column. */
export const RESOURCES_WITH_COUNTS_AS_TOUCH = ["contact_notes"] as const;

/**
 * True only for note resources that have the column.
 *
 * Deliberately an allowlist, not `resource !== "deal_notes"`. A denylist says
 * yes to everything it has not heard of, so the next note-like resource
 * upstream adds would default to submitting a column it does not have -- the
 * failure above, arriving through a merge rather than an edit.
 */
export function hasCountsAsTouch(resource: string | undefined): boolean {
  if (!resource) return false;
  return (RESOURCES_WITH_COUNTS_AS_TOUCH as readonly string[]).includes(
    resource,
  );
}

/**
 * What a new note gets when nobody touches the checkbox.
 *
 * TRUE, matching the database default, and the two must not drift. Most notes
 * do record contact, so opt-out is the honest default -- and if the UI sent
 * false by default while the column defaulted true, notes written on mobile
 * (no checkbox) and notes written on desktop (checkbox, untouched) would
 * record different things about the same act.
 */
export const COUNTS_AS_TOUCH_DEFAULT = true;
